import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useKundeAnlegen, useKunden, useKundenarten } from '../api/abfragen'

import { Adressuebernahme } from '../bausteine/Adressuebernahme'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis } from '../bausteine/Hinweise'
import { darfSchreiben, useKonto } from '../konto/KontoKontext'
import { Anlagetunnel } from '../anlage/Anlagetunnel'

export function Kunden() {
  const { konto } = useKonto()
  const kunden = useKunden()
  const anlegen = useKundeAnlegen()
  const arten = useKundenarten()
  const [formularOffen, setzeFormularOffen] = useState(false)
  /* Das Formular als Zustand und nicht als `useRef`.
   *
   * Die Adressübernahme sitzt darin und braucht es beim Rendern; ein
   * `useRef` ist beim ersten Durchlauf noch leer und löst keine
   * Neuzeichnung aus, wenn es gefüllt wird. */
  const [formular, setzeFormular] = useState<HTMLFormElement | null>(null)
  const [tunnelOffen, setzeTunnelOffen] = useState(false)
  const [suche, setzeSuche] = useState('')

  const gefiltert = (kunden.data ?? []).filter((k) =>
    `${k.name} ${k.ort ?? ''} ${k.kundennummer ?? ''}`
      .toLowerCase()
      .includes(suche.trim().toLowerCase()),
  )

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-2xl font-bold">Kunden</h1>
        {darfSchreiben(konto) && (
          <div className="ml-auto flex flex-wrap gap-2">
            {/* Der geführte Weg ist der Hauptknopf: ein neuer Kunde bringt
                fast immer Plätze und Geräte mit, und einzeln angelegt
                vergisst man die Hälfte. Das kurze Formular bleibt daneben
                für den Fall, dass nur der Name gebraucht wird. */}
            <button
              type="button"
              className="knopf-haupt"
              onClick={() => setzeTunnelOffen(true)}
            >
              Neuer Kunde
            </button>
            <button
              type="button"
              className="knopf-still px-3"
              onClick={() => setzeFormularOffen((o) => !o)}
            >
              {formularOffen ? 'Abbrechen' : 'Nur Name'}
            </button>
          </div>
        )}
      </header>

      {formularOffen && (
        <form
          ref={setzeFormular}
          className="karte space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            const formular = new FormData(e.currentTarget)
            const daten = Object.fromEntries(
              [...formular.entries()].filter(([, wert]) => String(wert).trim() !== ''),
            )
            anlegen.mutate(daten as never, {
              onSuccess: () => {
                setzeFormularOffen(false)
                anlegen.reset()
              },
            })
          }}
        >
          <Adressuebernahme formular={formular} />

          <div>
            <label htmlFor="name">Name</label>
            <input id="name" name="name" required placeholder="Gemeinde Krummhörn" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="art">Art</label>
              <select id="art" name="art" defaultValue="">
                <option value="">— keine Angabe —</option>
                {(arten.data ?? []).map((a) => (
                  <option key={a.id} value={a.name}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="kundennummer">Kunden-Nr.</label>
              <input id="kundennummer" name="kundennummer" placeholder="K-2026-014" />
            </div>
            <div>
              <label htmlFor="strasse">Straße</label>
              <input id="strasse" name="strasse" />
            </div>
            <div className="grid grid-cols-[7rem_1fr] gap-3">
              <div>
                <label htmlFor="plz">PLZ</label>
                <input id="plz" name="plz" inputMode="numeric" />
              </div>
              <div>
                <label htmlFor="ort">Ort</label>
                <input id="ort" name="ort" />
              </div>
            </div>
            <div>
              <label htmlFor="ansprechpartner">Ansprechpartner</label>
              <input id="ansprechpartner" name="ansprechpartner" />
            </div>
            <div>
              <label htmlFor="email">E-Mail</label>
              <input id="email" name="email" type="email" autoCapitalize="none" />
            </div>
            <div>
              <label htmlFor="telefon">Telefon</label>
              <input id="telefon" name="telefon" inputMode="tel" />
            </div>
          </div>

          <Fehlerhinweis fehler={anlegen.error} />

          <button type="submit" className="knopf-haupt" disabled={anlegen.isPending}>
            {anlegen.isPending ? 'Legt an …' : 'Kunde anlegen'}
          </button>
        </form>
      )}

      <input
        type="search"
        value={suche}
        onChange={(e) => setzeSuche(e.target.value)}
        placeholder="Suchen nach Name, Ort oder Nummer"
        aria-label="Kunden durchsuchen"
      />

      {kunden.isLoading ? (
        <Ladeanzeige />
      ) : kunden.error ? (
        <Fehlerhinweis fehler={kunden.error} />
      ) : gefiltert.length === 0 ? (
        <Leerhinweis
          titel={suche ? 'Kein Treffer' : 'Noch keine Kunden'}
          text={suche ? 'Andere Schreibweise versuchen.' : 'Legen Sie den ersten Kunden an.'}
        />
      ) : (
        <ul className="space-y-2">
          {gefiltert.map((k) => (
            <li key={k.id}>
              <Link to={`/kunden/${k.id}`} className="karte-knopf">
                <div className="flex-1">
                  <div className="font-semibold">{k.name}</div>
                  <div className="text-base text-slate-700">
                    {k.art ?? '—'}
                    {k.ort ? ` · ${k.ort}` : ''}
                    {k.kundennummer ? ` · ${k.kundennummer}` : ''}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{k.anzahl_spielplaetze}</div>
                  <div className="text-sm text-slate-600">
                    {k.anzahl_spielplaetze === 1 ? 'Platz' : 'Plätze'}
                  </div>
                </div>
                {k.offene_maengel > 0 && (
                  <span className="marke bg-stufe-3-grund text-stufe-3">
                    {k.offene_maengel}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {tunnelOffen && <Anlagetunnel schliessen={() => setzeTunnelOffen(false)} />}
    </div>
  )
}
